import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { playlistApi } from '../../api/playlist';
import { useUserStore } from '../../stores/userStore';
import useBadgeSession, { isBadgeSessionCurrent } from '../../features/badges/useBadgeSession';
import PlaylistArtwork from './PlaylistArtwork';

// DTO-based search/profile rows and canonical Explore cards opt in. Drafts and cards that
// already own a detail query keep the pure renderer and cannot trigger extra reads.
function QueriedArtwork({ playlist, session, authenticated, canonical, children }) {
  const slug = playlist.slug?.trim();
  const key = slug || playlist.id;
  const current = () => isBadgeSessionCurrent(session);
  const detail = useQuery({
    queryKey: ['viewer-playlist-artwork', session, slug ? 'slug' : 'id', key],
    enabled: Boolean(key),
    queryFn: async ({ signal }) => {
      const assertCurrent = () => {
        if (signal.aborted || !current())
          throw new DOMException('Artwork session changed', 'AbortError');
      };
      assertCurrent();
      const config = {
        signal,
        optionalAuth: authenticated,
        suppressGlobalError: true,
        skipAuthRecovery: true,
        isSessionCurrent: current,
      };
      const response = await (slug
        ? playlistApi.getPlaylistBySlug(slug, config)
        : playlistApi.getPlaylistById(playlist.id, config));
      assertCurrent();
      return response.data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const data = current() && !detail.isError ? detail.data : undefined;
  const previewImageUrls = [
    ...new Set(
      [...(data?.albums || []), ...(data?.tracks || [])]
        .sort((a, b) => a.position - b.position)
        .map((item) => item.albumImageUrl)
        .filter(Boolean)
        .concat(canonical ? playlist.previewImageUrls || [] : [])
        .filter(Boolean),
    ),
  ].slice(0, 4);
  return children(
    { ...playlist, imageUrl: playlist.imageUrl?.trim() || data?.imageUrl, previewImageUrls },
    data,
  );
}

const renderArtwork = (playlist) => <PlaylistArtwork playlist={playlist} />;

export default function HydratedPlaylistArtwork({
  playlist,
  canonical = false,
  children = renderArtwork,
}) {
  const session = useBadgeSession();
  const authenticated =
    useUserStore((s) => s.user?.id != null && s.isLogin) &&
    Boolean(localStorage.getItem('POSTPOP_AUTH_TOKEN'));
  if (!canonical && (playlist.imageUrl?.trim() || playlist.previewImageUrls?.some(Boolean))) {
    return children(playlist);
  }
  return (
    <QueriedArtwork
      key={session}
      playlist={playlist}
      session={session}
      authenticated={authenticated}
      canonical={canonical}
    >
      {children}
    </QueriedArtwork>
  );
}
